import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import {
  FactCheck as VerificationIcon,
  Analytics as AnalyticsIcon,
  SwapHoriz as AdjustmentIcon,
  Description as CertificateIcon,
} from "@mui/icons-material";
import { SelectClientBanner } from "@/components";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { layoutDimensions } from "@/utils/responsive";
import { tabSx } from "@/utils/customs-helpers";
import { tokens } from "@/tokens";
import { COMPLIANCE_TABS, COMPLIANCE_TAB_KEYS } from "@/constants";
import { useFilteredTabs } from "@/hooks/useFilteredTabs";

const TAB_ICON_MAP: Record<string, React.ReactElement> = {
  verification: <VerificationIcon sx={{ fontSize: 18 }} />,
  "tariff-analysis": <AnalyticsIcon sx={{ fontSize: 18 }} />,
  "post-import": <AdjustmentIcon sx={{ fontSize: 18 }} />,
  "certificate-of-origin": <CertificateIcon sx={{ fontSize: 18 }} />,
};

const Compliance: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const location = useLocation();
  const navigate = useNavigate();
  const filteredTabs = useFilteredTabs(COMPLIANCE_TABS, COMPLIANCE_TAB_KEYS, "COMPLIANCE");

  const activeTab = useMemo(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const idx = filteredTabs.findIndex((tab) => tab.link === currentPath);
    return idx === -1 ? 0 : idx;
  }, [location.pathname, filteredTabs]);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const found = filteredTabs.some((tab) => tab.link === currentPath);
    if (!found && filteredTabs.length > 0) {
      navigate(`/compliance/${filteredTabs[0].link}`, { replace: true });
    }
  }, [location.pathname, filteredTabs, navigate]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = filteredTabs[newValue];
    if (!tab) return;
    navigate(`/compliance/${tab.link}`);
  };

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  return (
    <Stack
      spacing={0}
      sx={{
        // Use negative margins that match the layout padding to go edge-to-edge
        margin: {
          xs: `calc(-1 * ${layoutDimensions.layoutPaddingSmall}) calc(-1 * ${layoutDimensions.layoutPaddingMedium})`,
          md: `calc(-1 * ${layoutDimensions.layoutPaddingMedium}) calc(-1 * ${layoutDimensions.layoutPaddingLarge})`,
        },
        bgcolor: tokens.bg.muted,
        width: {
          xs: `calc(100% + 2 * ${layoutDimensions.layoutPaddingMedium})`,
          md: `calc(100% + 2 * ${layoutDimensions.layoutPaddingLarge})`,
        },
        height: {
          xs: `calc(100vh - ${layoutDimensions.layoutPaddingSmall} - ${layoutDimensions.layoutPaddingSmall})`,
          md: `calc(100vh - ${layoutDimensions.layoutPaddingMedium} - ${layoutDimensions.layoutPaddingMedium})`,
        },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Main Tabs - Top of page */}
      <Box sx={{
        borderBottom: '1px solid #e2e5e9',
        margin: 0,
        padding: 0,
        width: '100%',
        bgcolor: tokens.bg.muted,
        flexShrink: 0,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 48, px: { xs: 1.5, md: 3 }, margin: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              maxWidth: '100%',
              '& .MuiTabs-indicator': {
                backgroundColor: '#0a0a0f',
                height: 2,
                bottom: 0,
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            {filteredTabs.map((tab) => (
              <Tab
                key={tab.link}
                label={tab.text}
                icon={TAB_ICON_MAP[tab.link]}
                iconPosition="start"
                sx={{ ...tabSx, gap: 0.75 }}
              />
            ))}
          </Tabs>
        </Stack>
      </Box>

      {/* Tab Content - fills remaining height, child handles scrolling */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        boxSizing: 'border-box',
        bgcolor: tokens.bg.muted,
      }}>
        <Outlet />
      </Box>
    </Stack>
  );
};

export default Compliance;
